import { userErrorMessage } from '@wx-weiqi/contracts';
import { useCallback, useEffect, useId, useRef, useState, type ReactNode } from 'react';
import { ChevronDownIcon, ChevronUpIcon } from '@radix-ui/react-icons';
import type { StudentRoomMember } from '@wx-weiqi/contracts';
import type { StudentActivityApi } from '../services/student-activity';
import './student-activity.css';

const EMPTY_MEMBERS: readonly StudentRoomMember[] = [];

export function peoplePageSize(height: number) {
  return Math.max(1, Math.min(12, Math.floor((height - 88) / 44)));
}

/** Serial polling; old responses cannot leak into a different game, assignment or page. */
export function useStudentPolling<T>(load: () => Promise<T>, intervalMs = 5000) {
  const [data, setData] = useState<T | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [pending, setPending] = useState(true);
  const [revision, setRevision] = useState(0);
  useEffect(() => {
    let disposed = false;
    let timer: ReturnType<typeof setTimeout>;
    setData(null);
    setError(null);
    setPending(true);
    const schedule = () => {
      timer = setTimeout(() => {
        if (document.hidden) schedule();
        else void poll();
      }, intervalMs);
    };
    const poll = async () => {
      try {
        const result = await load();
        if (!disposed) {
          setData(result);
          setError(null);
        }
      } catch (cause) {
        if (!disposed) {
          setData(null);
          setError(userErrorMessage(cause, '加载失败，请重试。'));
        }
      } finally {
        if (!disposed) {
          setPending(false);
          schedule();
        }
      }
    };
    void poll();
    return () => {
      disposed = true;
      clearTimeout(timer);
    };
  }, [load, intervalMs, revision]);
  return { data, error, pending, refresh: () => setRevision((value) => value + 1) };
}

function PeopleCard({
  title,
  load,
  emptyText,
  revision = 0,
  collapsible = false,
}: {
  readonly title: string;
  readonly load: (
    page: number,
    pageSize: number,
  ) => Promise<{
    total: number;
    rows: readonly { id: string; name: string; detail: ReactNode; complete?: boolean }[];
  }>;
  readonly emptyText: string;
  readonly revision?: number;
  readonly collapsible?: boolean;
}) {
  const card = useRef<HTMLElement>(null);
  const contentId = useId();
  const [expanded, setExpanded] = useState(!collapsible);
  const [pageSize, setPageSize] = useState(3);
  const measuredPageSize = useRef(3);
  const [page, setPage] = useState(1);
  useEffect(() => {
    if (card.current === null) return;
    const observer = new ResizeObserver((entries) => {
      const height = entries[0]?.contentRect.height;
      if (height === undefined || (collapsible && !expanded)) return;
      const next = peoplePageSize(height);
      if (next !== measuredPageSize.current) {
        measuredPageSize.current = next;
        setPageSize(next);
        setPage(1);
      }
    });
    observer.observe(card.current);
    return () => observer.disconnect();
  }, [collapsible, expanded]);
  const fetchPage = useCallback(() => {
    void revision;
    return load(page, pageSize);
  }, [load, page, pageSize, revision]);
  const state = useStudentPolling(fetchPage);
  const pages = Math.max(1, Math.ceil((state.data?.total ?? 0) / pageSize));
  useEffect(() => {
    if (state.data !== null && page > pages) setPage(pages);
  }, [page, pages, state.data]);
  return (
    <section
      className={`student-people-card${collapsible ? ' is-collapsible' : ''}${collapsible && !expanded ? ' is-collapsed' : ''}`}
      ref={card}
      aria-label={title}
    >
      <header>
        {collapsible ? (
          <h2>
            <button
              className="student-people-toggle"
              type="button"
              aria-expanded={expanded}
              aria-controls={contentId}
              onClick={() => setExpanded((current) => !current)}
            >
              <span>{title}</span>
              <small>{state.data === null ? '—' : `${state.data.total} 人`}</small>
              {expanded ? (
                <ChevronUpIcon aria-hidden="true" />
              ) : (
                <ChevronDownIcon aria-hidden="true" />
              )}
            </button>
          </h2>
        ) : (
          <>
            <h2>{title}</h2>
            <span>{state.data === null ? '—' : `${state.data.total} 人`}</span>
          </>
        )}
        <button
          type="button"
          onClick={state.refresh}
          aria-label={`刷新${title}`}
          disabled={state.pending}
          hidden={!expanded}
        >
          刷新
        </button>
      </header>
      <div className="student-people-body" id={contentId} aria-live="polite" hidden={!expanded}>
        {state.error !== null ? (
          <p role="alert">{state.error}</p>
        ) : state.pending ? (
          <p>正在加载…</p>
        ) : state.data?.rows.length === 0 ? (
          <p>{emptyText}</p>
        ) : (
          <ul>
            {state.data?.rows.map((row) => (
              <li key={row.id}>
                <strong title={row.name}>{row.name}</strong>
                <span className={row.complete ? 'is-complete' : ''}>{row.detail}</span>
              </li>
            ))}
          </ul>
        )}
      </div>
      <footer hidden={!expanded}>
        <button
          type="button"
          disabled={page <= 1 || state.pending}
          onClick={() => setPage((current) => current - 1)}
          aria-label={`${title}上一页`}
        >
          上一页
        </button>
        <span>
          {page} / {pages}
        </span>
        <button
          type="button"
          disabled={page >= pages || state.pending}
          onClick={() => setPage((current) => current + 1)}
          aria-label={`${title}下一页`}
        >
          下一页
        </button>
      </footer>
    </section>
  );
}

export function StudentRoomMembersCard({
  api,
  gameId,
  initialMembers = EMPTY_MEMBERS,
  collapsible = false,
}: {
  readonly api?: StudentActivityApi | undefined;
  readonly gameId: string;
  readonly collapsible?: boolean;
  readonly initialMembers?: readonly StudentRoomMember[];
}) {
  const fallbackMembers = api === undefined ? initialMembers : EMPTY_MEMBERS;
  const load = useCallback(
    async (page: number, pageSize: number) => {
      const members = api === undefined ? fallbackMembers : (await api.roomMembers(gameId)).members;
      return {
        total: members.length,
        rows: members.slice((page - 1) * pageSize, page * pageSize).map((member) => ({
          id: member.membershipId,
          name: member.displayName,
          detail: `${member.role === 'BLACK' ? '黑方' : member.role === 'WHITE' ? '白方' : '观战'} · ${member.online ? '在线' : '离线'}`,
        })),
      };
    },
    [api, gameId, fallbackMembers],
  );
  return (
    <PeopleCard
      key={gameId}
      collapsible={collapsible}
      title={collapsible ? '本房间成员' : '房间列表'}
      load={load}
      emptyText="暂无房间人员"
    />
  );
}

export function StudentHomeworkParticipantsCard({
  api,
  assignmentId,
  revision,
  collapsible = false,
}: {
  readonly api?: StudentActivityApi | undefined;
  readonly assignmentId: string;
  readonly revision: number;
  readonly collapsible?: boolean;
}) {
  const load = useCallback(
    async (page: number, pageSize: number) => {
      if (api === undefined) throw new Error('作答列表暂不可用。');
      const result = await api.homeworkParticipants(assignmentId, page, pageSize);
      return {
        total: result.total,
        rows: result.participants.map((participant) => ({
          id: participant.membershipId,
          name: participant.displayName,
          detail: `${participant.completedProblems}/${participant.totalProblems} · ${participant.status === 'COMPLETED' ? '已完成' : participant.status === 'IN_PROGRESS' ? '作答中' : '未完成'}`,
          complete: participant.status === 'COMPLETED',
        })),
      };
    },
    [api, assignmentId],
  );
  return (
    <PeopleCard
      key={assignmentId}
      title="作答列表"
      load={load}
      emptyText="暂无作答人员"
      revision={revision}
      collapsible={collapsible}
    />
  );
}
